import React from 'react'
import styled, {DefaultTheme} from 'styled-components'
import type {MarginProps, ResponsiveValue, StyleProps} from '../../'
import {convertLayoutProps} from '../../utils'

export interface HeadingProps extends MarginProps {
  as?: React.ElementType
  $styles?: StyleProps
  color?: keyof DefaultTheme['colors']
  className?: string
  children: React.ReactNode
  $variant?: ResponsiveValue<keyof DefaultTheme['typography']['heading']>
  $align?: ResponsiveValue<'left' | 'right' | 'center'>
}

const HeadingElement = React.forwardRef<HTMLElement, HeadingProps>((props, ref) => {
  const {as: Element = 'div', children, ...restProps} = props

  return (
    <Element ref={ref} {...restProps}>
      {children}
    </Element>
  )
})

HeadingElement.displayName = 'HeadingElement'

const HeadingBase = styled(HeadingElement)`
  font-family: var(--wcds-font-primary);
  font-weight: var(--wcds-legacy-heading-base-font-weight);
  font-feature-settings: var(--wcds-header-font-feature-settings);

  overflow-wrap: break-word;
  word-wrap: break-word;

  body:lang(ja) & {
    font-family: ${({theme}) => theme.fonts.mPlus1};
    font-feature-settings: normal;
  }

  body:lang(ru) & {
    font-family: var(--wcds-font-secondary);
    font-feature-settings: 'ss03';
  }
`

HeadingBase.displayName = 'HeadingBase'

const Heading = styled(HeadingBase)<HeadingProps>`
  color: ${({theme, color = 'primaryIndigo'}) => theme.colors[color]};

  ${({theme, $styles, $align, ...props}) =>
    theme.normalizeStyles({...convertLayoutProps(props), textAlign: $align, ...$styles})};

  ${({theme, $variant}) => $variant && theme.expandVariants($variant, theme.typography.heading)}
`

Heading.displayName = 'Heading'

export default Heading
